import { useEffect, useRef, useState } from "react";

import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import * as geometryEngineAsync from "@arcgis/core/geometry/geometryEngineAsync";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";
import Graphic from "@arcgis/core/Graphic";
import { geojsonToArcGIS } from "@terraformer/arcgis";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectionLineSymbol } from "../components/Dashboard/Editor/CoordinatesSelection/RoadsCoordinates";
import { queryLayer } from "../components/Dashboard/Editor/helpers";
import { graphicsLayer, view } from "../utils/API";
import { fetchDirections } from "../utils/ORS_API";
import useCustomSnackbar from "./useCustomSnackbar";

// Add this helper function before useRouteCalculation
const generateRouteKey = (coordinates) => {
  return coordinates.map((coord) => coord.join("|")).join("-");
};

const useRouteCalculation = (
  clickedPoints,
  setAddedPoints,
  setCount,
  matchBaselineRoads,
  requestHandle
) => {
  const [loading, setLoading] = useState(false);
  // const [startLookingPossibleRoutes, setStartLookingPossibleRoutes] = useState(false);
  const [errorPoints, setErrorPoints] = useState([]);
  const { editableLayer } = useSelector((state) => state.dashboard);
  const ref = useRef(true);
  const [openSnackbar] = useCustomSnackbar();
  const { t } = useTranslation("common");
  const routeCache = useRef(new Map());

  const layerControllers = useRef({});

  const handleCreateRoute = async () => {
    setLoading(true);
    if (clickedPoints.length < 2) return;

    const allPoints = graphicsLayer.graphics
      .filter((g) => g.id && g.id.includes("point"))
      .toArray();
    const pointsPolyline = new Polyline({
      paths: allPoints.map((p) => [p.geometry.longitude, p.geometry.latitude]),
    });
    if (pointsPolyline.extent) {
      await view.goTo(pointsPolyline.extent.expand(1.5), {
        duration: 1500,
      });
    }

    const routePoints = [];
    const graphics = [];
    const graphicsMap = {};
    graphicsLayer.graphics.forEach((g) => {
      if (g.id && g.id.includes("route") && !g.id.includes("route-manual")) {
        graphics.push(g);
      }
    });

    graphicsLayer.removeMany(graphics);
    clickedPoints.forEach((point, index, array) => {
      let nextPoint;
      if (index < array.length - 1) {
        nextPoint = array[index + 1];
      }

      if (nextPoint?.searchable && point.searchable) {
        const id =
          point.coordinate.join("|") + "-" + nextPoint.coordinate.join("|");

        routePoints.push({
          id: point.coordinate.join("|") + "-" + nextPoint.coordinate.join("|"),
          coordinate: [point.coordinate, nextPoint.coordinate],
          points: [point, nextPoint],
        });
      }
    });

    const routePromises = routePoints.map((route) => {
      const cacheKey = generateRouteKey(route.coordinate);

      if (routeCache.current.has(cacheKey)) {
        return Promise.resolve(routeCache.current.get(cacheKey));
      }

      return fetchDirections({
        coordinates: route.coordinate,
        radiuses: [6000],
        extra_info: [
          "osmid",
          "waytype",
          "waycategory",
          "roadaccessrestrictions",
          "countryinfo",
          "suitability",
          "surface",
        ],
        preference: "recommended",
      }).then(async (response) => {
        const data = await response.json();
        routeCache.current.set(cacheKey, { data });
        return { data };
      });

      // return esriRequest(
      //   `${OPEN_ROUTE_SERVICE_API}/v2/directions/driving-car/geojson`,
      //   {
      //     method: "POST",
      //     responseType: "json",
      //     body: JSON.stringify({
      //       coordinates: route.coordinate,
      //       radiuses: [6000],
      //       extra_info: [
      //         "osmid",
      //         "waytype",
      //         "waycategory",
      //         "roadaccessrestrictions",
      //         "countryinfo",
      //         "suitability",
      //         "surface",
      //       ],
      //       preference: "shortest",
      //     }),
      //     headers: {
      //       Authorization: OPEN_ROUTE_SERVICE_API_KEY,
      //       "Content-type": "application/json",
      //     },
      //   }
      // ).then((response) => {
      //   routeCache.current.set(cacheKey, response);
      //   return response;
      // });
    });
    const routePolylines = [];
    try {
      const routes = await Promise.all(routePromises);
      const routeGraphics = [],
        routeGraphicPromises = [];
      const routeFeatures = [];

      routes.forEach((route, index) => {
        const id = routePoints[index].id;
        const points = routePoints[index].points;
        const { data } = route;
        if (!data) return;

        const [feature] = geojsonToArcGIS(data);
        const polyline = new Polyline({
          paths: feature.geometry.paths,
          spatialReference: feature.spatialReference,
        });
        if (!matchBaselineRoads) {
          feature.id = id;
          routeFeatures.push(feature);
        } else {
          routeGraphicPromises.push(handleQueryLayer(polyline, id, points));
        }
      });

      if (!matchBaselineRoads) {
        await addRouteGraphic(routeFeatures, clickedPoints);
      } else {
        const result = await Promise.allSettled(routeGraphicPromises);
      }
    } catch (err) {
      setTimeout(() => {
        requestHandle?.onError();
      }, 1000);
    } finally {
      requestHandle.onFinish();
    }
  };

  const addRouteGraphic = async (
    routeFeatures,
    clickedPoints,
    routeFeature
  ) => {
    const cities = clickedPoints.filter((p) => !!p.city).map((p) => p.city);

    const routeGraphics = [];
    routeFeatures.forEach((feature) => {
      const polyline = new Polyline({
        paths: feature.geometry.paths,
        spatialReference: feature.spatialReference,
      });

      routeGraphics.push({
        id: "route-" + feature.id,
        symbol: selectionLineSymbol,
        geometry: polyline,
        cities,
        orsFeature: feature,
      });
    });

    graphicsLayer.addMany(routeGraphics);
  };

  const findAllLines = (clickedPoints) => {
    const possibleRoutes = [];
    const promises = [];
    clickedPoints.forEach((start, i) => {
      const allCoordinates = [];
      if (clickedPoints.length - 1 > i) {
        clickedPoints.forEach((destination, index) => {
          if (i !== index) {
            const coordinates = [start, destination];
            allCoordinates.push(coordinates);
          }
        });
      }
      possibleRoutes.push(allCoordinates);
    });

    possibleRoutes.forEach((arr) => {
      arr.forEach((coordinates) => {
        promises.push(handleRequest(coordinates));
      });
    });

    return Promise.all(promises);
  };

  const handleRequest = async (points) => {
    try {
      const response = await fetchDirections({
        coordinates: points.map((item) => item.coordinate),
        radiuses: [6000],
      });
      return await response.json();
    } catch (err) {
      return null;
    }
    // try {
    //   const route = await esriRequest(
    //     `${OPEN_ROUTE_SERVICE_API}/v2/directions/driving-car/geojson`,
    //     {
    //       method: "POST",
    //       responseType: "json",
    //       body: JSON.stringify({
    //         coordinates: points.map((item) => item.coordinate),
    //         radiuses: [6000],
    //       }),
    //       headers: {
    //         Authorization: OPEN_ROUTE_SERVICE_API_KEY,
    //         "Content-type": "application/json",
    //       },
    //     }
    //   );
    //   return route;
    // } catch (err) {
    //   return null;
    // }
  };

  const handleQueryLayer = async (polyline, id, clickedPoints) => {
    let requestedPolylineGraphic;
    const layer = view.map.layers.find(
      (l) => l.layerConfig?.titleLabel === "roads"
    );

    const roadsBaselineLayers = view.map.layers.filter(
      (l) => l.layerConfig?.titleLabel === "roads"
    );

    const drawBuffer = await geometryEngineAsync.geodesicBuffer(
      polyline,
      100,
      "meters"
    );

    const drawBufferClient = await geometryEngineAsync.geodesicBuffer(
      polyline,
      10000,
      "meters"
    );

    roadsBaselineLayers.forEach((layer) => {
      layerControllers.current[layer.id] = new AbortController();
    });

    const queryRes = await queryLayer(
      layer,
      drawBuffer,
      0,
      polyline,
      editableLayer,
      roadsBaselineLayers,
      setCount,
      drawBufferClient,
      id,
      {
        controllers: layerControllers.current,
        geometryEngine,
        geometryEngineAsync,
        Polyline,
        Graphic,
        webMercatorUtils,
        Point,
      }
    );

    if (!queryRes) {
      openSnackbar(t("screen.message.error"));
      return;
    }

    const {
      graphic: res,
      cuttedFeatures,
      trimmedSituationalFeatures,
      deleteSituationalFeatures,
    } = queryRes;

    requestedPolylineGraphic = res;
    const geometry = webMercatorUtils.geographicToWebMercator(
      requestedPolylineGraphic.geometry
    );
    requestedPolylineGraphic.geometry = geometry;
    requestedPolylineGraphic.id = "route-" + id;
    const apiStartPoint = webMercatorUtils.geographicToWebMercator(
      polyline.getPoint(0, 0)
    );
    const apiEndPoint = webMercatorUtils.geographicToWebMercator(
      polyline.getPoint(0, polyline.paths[0].length - 1)
    );
    const points = [apiStartPoint, apiEndPoint];

    const promises = points.map((p) => {
      return geometryEngineAsync.nearestVertex(
        requestedPolylineGraphic.geometry,
        p
      );
    });

    const pointResults = await Promise.all(promises);

    const paths = [];

    pointResults.forEach((coordinateResult, index) => {
      const point = points[index];

      paths.push([
        [point.x, point.y],
        [coordinateResult.coordinate.x, coordinateResult.coordinate.y],
      ]);
    });

    const pointsPolyline = new Polyline({
      paths: paths,
      spatialReference: view.spatialReference,
    });

    const unioned = await geometryEngineAsync.union([
      pointsPolyline,
      requestedPolylineGraphic.geometry,
    ]);

    const graphics = [];
    const cities = [];

    for (const feat of cuttedFeatures) {
      let geometry = feat.geometry;

      const graphic = new Graphic({
        id: requestedPolylineGraphic.id,
        geometry: geometry,
        attributes: {
          ...(feat.attributes || {}),
          cities: cities,
        },
        symbol: requestedPolylineGraphic.symbol,
      });
      graphics.push(graphic);
    }

    graphicsLayer.addMany(graphics);

    requestedPolylineGraphic.geometry = unioned;

    requestHandle?.onSuccess({
      bufferNumber: bufferWidth,
      geometry,
      cuttedFeatures,
    });
  };

  useEffect(() => {
    return () => {
      Object.keys(layerControllers.current).forEach((key) => {
        layerControllers.current[key].abort();
      });
    };
  }, []);

  return {
    loading,
    setLoading,
    handleCreateRoute,
    errorPoints,
  };
};

export default useRouteCalculation;
